import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Loader2, RefreshCw, Search, AlertTriangle, ClipboardList, ChevronRight, Plus,
} from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ComposeModal from "@/components/ComposeModal";
import { useLivePoll } from "@/hooks/useLivePoll";

const ADMIN_ROLES = ["admin", "control", "planner", "superintendent", "supervisor", "owner"];

/**
 * Inspections page — list of every inspection at the current site.
 * Clicking a row opens the pixel-perfect full-screen InspectionDetail
 * (matches the mobile app: stats bar, section headers, task cards with
 * OK/ISSUE/N/A buttons, Pin Matrix, Grouser inputs, sign-off & send-back).
 */
export default function Inspections() {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  // Filter state — Josh (Feb 27): "add a global 'Completed Inspections' feed
  // that shows ALL inspections across all crews, all sites, all shifts."
  // `shiftFilter` = "all" | "day" | "night"; `statusFilter` = "all" | "completed" | "in_progress".
  const [shiftFilter, setShiftFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const location = useLocation();
  const navigate = useNavigate();

  const reload = async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await api.get("/inspections");
      const arr = Array.isArray(data) ? data : data?.items || data?.results || data?.data || [];
      setItems(arr);
    } catch (ex) { setErr(apiError(ex)); setItems([]); }
    finally { setBusy(false); }
  };
  useEffect(() => { reload(); }, []);
  useLivePoll(reload, 5000, true);

  // Deep-link — Sign-off Inbox opens ?open={id}&signoff=1
  useEffect(() => {
    if (!items || items.length === 0) return;
    const params = new URLSearchParams(location.search);
    const openId = params.get("open");
    if (!openId) return;
    // Full-page mode (Feb 23) — deep-link straight to the detail page.
    const match = items.find((it) => (it.id || it._id) === openId);
    if (!match) return;
    navigate(`/inspections/${match.id || match._id}${params.get("signoff") ? "?signoff=1" : ""}`, { replace: true });
  }, [items, location.search, location.pathname, navigate]);

  const canCreate = useMemo(() => {
    const r = String(user?.role || "").toLowerCase();
    return user?.is_admin || ADMIN_ROLES.some((x) => r.includes(x));
  }, [user]);

  const filtered = useMemo(() => {
    let list = items || [];
    if (shiftFilter !== "all") {
      list = list.filter((it) => String(it.shift || "").toLowerCase() === shiftFilter);
    }
    if (statusFilter === "completed") {
      list = list.filter((it) => {
        const s = String(it.status || "").toLowerCase();
        return ["approved", "signed", "closed", "complete", "submitted"].some((k) => s.includes(k));
      });
    } else if (statusFilter === "in_progress") {
      list = list.filter((it) => {
        const s = String(it.status || "").toLowerCase();
        return !["approved", "signed", "closed", "complete"].some((k) => s.includes(k));
      });
    }
    if (q) {
      const s = q.toLowerCase();
      list = list.filter((it) => {
        const hay = `${it.template_name || ""} ${it.asset_id || ""} ${it.status || ""} ${it.shift || ""} ${it.assigned_crew_name || ""} ${it.started_by_name || ""}`.toLowerCase();
        return hay.includes(s);
      });
    }
    return list.sort((a, b) => new Date(b.updated_at || b.created_at || 0) - new Date(a.updated_at || a.created_at || 0));
  }, [items, q, shiftFilter, statusFilter]);

  // Completed count for the tab badge — computed once from the raw list so
  // both filter tabs show a live number.
  const completedCount = useMemo(() => {
    return (items || []).filter((it) => {
      const s = String(it.status || "").toLowerCase();
      return ["approved", "signed", "closed", "complete", "submitted"].some((k) => s.includes(k));
    }).length;
  }, [items]);

  return (
    <div className="space-y-5" data-testid="inspections-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] font-black text-muted">Compliance</div>
          <h1 className="font-display text-4xl md:text-5xl font-black tracking-tight leading-none mt-1">Inspections</h1>
          <div className="text-muted mt-2 text-sm font-mono-tight">/inspections · Site-scoped · Tap a row to open</div>
        </div>
        <div className="flex gap-2">
          <button className="btn btn-sm btn-ghost" onClick={reload} disabled={busy} data-testid="btn-refresh">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Refresh
          </button>
          {canCreate && (
            <button className="btn btn-sm btn-primary" onClick={() => setShowCreate(true)} data-testid="btn-new">
              <Plus className="w-4 h-4" /> New Inspection
            </button>
          )}
        </div>
      </div>

      <div className="panel-flat px-3 flex items-center gap-2">
        <Search className="w-4 h-4 text-muted shrink-0" />
        <input
          className="w-full py-3 bg-transparent outline-none text-base"
          placeholder="Search by asset ID, template, crew, tradesperson…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid="inspections-search"
        />
      </div>

      {/* Filter tabs — Josh (Feb 27): global feed across all crews / shifts.
          Two rows: status scope (All / Completed / In progress) + shift
          (All shifts / Day / Night). Filters compose so an owner can pull
          e.g. "every Completed Night inspection across every crew". */}
      <div className="flex flex-wrap gap-2" data-testid="insp-filter-row">
        <FilterPill active={statusFilter === "all"} onClick={() => setStatusFilter("all")} label={`All · ${items?.length ?? 0}`} testid="insp-filter-all" />
        <FilterPill active={statusFilter === "completed"} onClick={() => setStatusFilter("completed")} label={`Completed · ${completedCount}`} testid="insp-filter-completed" tone="green" />
        <FilterPill active={statusFilter === "in_progress"} onClick={() => setStatusFilter("in_progress")} label="In progress" testid="insp-filter-inprogress" tone="orange" />
        <div className="w-px h-6 bg-white/10 mx-1 self-center" />
        <FilterPill active={shiftFilter === "all"} onClick={() => setShiftFilter("all")} label="All shifts" testid="insp-shift-all" />
        <FilterPill active={shiftFilter === "day"} onClick={() => setShiftFilter("day")} label="Day" testid="insp-shift-day" />
        <FilterPill active={shiftFilter === "night"} onClick={() => setShiftFilter("night")} label="Night" testid="insp-shift-night" />
      </div>

      {err && <div className="pill pill-red !text-sm !py-2 !px-3 justify-start"><AlertTriangle className="w-3.5 h-3.5" /> {err}</div>}

      {items === null ? (
        <div className="panel-flat p-10 flex items-center justify-center gap-3 text-muted">
          <Loader2 className="w-5 h-5 animate-spin" /> Loading inspections…
        </div>
      ) : filtered.length === 0 ? (
        <div className="panel-flat p-10 text-center text-muted">
          <ClipboardList className="w-8 h-8 mx-auto mb-2 opacity-60" /> No inspections yet.
        </div>
      ) : (
        <div className="grid gap-2" data-testid="inspections-list">
          {filtered.map((it) => (
            <button
              key={it.id || it._id}
              className="panel p-4 flex items-center gap-4 text-left group hover:border-orange-500/40"
              onClick={() => navigate(`/inspections/${it.id || it._id}`)}
              data-testid={`inspection-row-${it.id || it._id}`}
            >
              <span className={`pill !text-[10px] !py-0.5 !px-2 uppercase font-black tracking-widest ${statusToneClass(it.status)}`}>
                {it.status || "draft"}
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="pill !text-[10px] !py-0.5 !px-2 font-mono-tight" style={{ background: "rgba(245,130,32,0.10)", border: "1px solid rgba(245,130,32,0.25)", color: "var(--primary)" }}>
                    {it.asset_id || "—"}
                  </span>
                  <span className="text-[10px] font-mono-tight text-muted">#{String(it.id || "").slice(0, 8)}</span>
                </div>
                <div className="font-display font-black text-base mt-1 truncate">{it.template_name || "Inspection"}</div>
                <div className="text-[11px] text-muted font-mono-tight mt-0.5 truncate">
                  {it.started_by_name || "—"} · {fmt(it.updated_at || it.started_at || it.created_at)}
                  {it.assigned_crew_name && <> · {it.assigned_crew_name}</>}
                  {it.shift && <> · {it.shift} shift</>}
                </div>
              </div>
              <ChevronRight className="w-4 h-4 text-muted group-hover:text-white" />
            </button>
          ))}
        </div>
      )}

      {/* Detail rendered via /inspections/:id — see App.js route + InspectionDetailPage.jsx.
          The full-page view is what Josh explicitly asked for on Feb 23:
          "just seams like it needs a full page to see the inspection". */}

      <ComposeModal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={reload}
        endpoint="/inspections"
        title="New Inspection"
        fields={[
          { name: "template_id", label: "Template", type: "select-async", required: true, optionsEndpoint: "/inspection-templates", optionLabel: (t) => t.name || `Template ${String(t.id || "").slice(0, 6)}` },
          { name: "asset_id", label: "Asset", type: "select-async", required: true, optionsEndpoint: "/assets", optionLabel: (a) => `${a.id || a.tag || a.name}${a.machine_model ? ` (${a.machine_model})` : ""}` },
          { name: "smu", label: "SMU (hour meter)" },
          { name: "shift", label: "Shift", type: "select", options: ["morning", "afternoon", "night", "day", "swing"] },
          { name: "notes", label: "Notes", type: "textarea", rows: 3 },
        ]}
        testid="inspections-create"
      />
    </div>
  );
}

function statusToneClass(s) {
  const v = String(s || "").toLowerCase();
  if (["approved", "signed", "closed", "complete"].some((k) => v.includes(k))) return "pill-green";
  if (["submit", "ready", "pending", "review"].some((k) => v.includes(k))) return "pill-orange";
  if (["back", "reject"].some((k) => v.includes(k))) return "pill-red";
  return "";
}
function fmt(v) { if (!v) return "—"; try { return new Date(v).toLocaleString(); } catch { return String(v); } }

function FilterPill({ active, onClick, label, tone, testid }) {
  const activeBg =
    tone === "green"  ? { bg: "rgba(74,222,128,0.18)", border: "rgba(74,222,128,0.55)", color: "#a7f3c9" } :
    tone === "orange" ? { bg: "rgba(245,130,32,0.18)", border: "rgba(245,130,32,0.55)", color: "#ffb347" } :
                        { bg: "rgba(255,255,255,0.10)", border: "rgba(255,255,255,0.35)", color: "#fff" };
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className="px-3 py-1.5 rounded-lg text-[11px] uppercase tracking-widest font-black transition-colors"
      style={active
        ? { background: activeBg.bg, border: `1px solid ${activeBg.border}`, color: activeBg.color }
        : { background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)", color: "rgba(255,255,255,0.65)" }}
    >
      {label}
    </button>
  );
}
